import styled, { css } from "styled-components";
import { Size, Variant } from "./types";
import { getTheme, } from "@bracket/ui/themes";

export const StyledButton = styled.button<{
	$size: Size;
	$variant: Variant;
	$disabled?: boolean;
	$interactive?: boolean;
	$busy?: boolean;
	$flexible?: boolean;
	$mt?: string;
	$mb?: string;
	$ml?: string;
	$mr?: string;
	$width?: string;
	$maxWidth?: string;
	$color?: string;
	$innerColor?: string;
}>`
	cursor: pointer;
	display: flex;
	justify-content: center;
	align-items: center;
	border: none;
	transition: all 0.3s ease;
	gap: 0.5rem;

	font-family: ${() => getTheme().fonts.body};

	${({ $flexible }) =>
		$flexible &&
		css`
			flex: 1;
		`}

	/* Margins and sizing */
  ${({ $mt, $mb, $ml, $mr, $width, $maxWidth }) => css`
		margin-top: ${$mt || "0"};
		margin-bottom: ${$mb || "0"};
		margin-left: ${$ml || "0"};
		margin-right: ${$mr || "0"};
		width: ${$width || "auto"};
		max-$width: ${$maxWidth || "initial"};
	`}


  /* $sizes */
  ${({ $size }) => {
		switch ($size) {
			case "sm":
				return css`
					height: 2rem;
					padding: 0.5rem 1rem;
					border-radius: 8px;
				`;
			case "md":
				return css`
					height: 2.75rem;
					padding: 0.75rem 1.25rem;
					border-radius: 8px;
				`;
			case "lg":
				return css`
					height: 3.5rem;
					padding: 1rem 1.5rem;
					border-radius: 8px;
				`;
			default:
				return "";
		}
	}}

  /* $variants */
  ${({ $variant }) => {
		switch ($variant) {
			case "primary":
				return css`
					background: ${() => getTheme().primary};
					color: ${() => getTheme().primary_on};
					&:hover:enabled {
						background: linear-gradient(0deg,
								${() => getTheme().tint_dark} 0%,
								${() => getTheme().tint_dark}  100%
							),
							${() => getTheme().primary};
					}
				`;
			case "primary-outline":
				return css`
					background: transparent;
					color: ${() => getTheme().primary};
					border: 1px solid ${() => getTheme().primary};

					&: $disabled {
						border: none;
					}

					&:hover:enabled {
						background: rgba(
							3,
							255,
							143,
							0.08
						);
					}
				`;
			case "secondary":
				return css`
					background: var(--secondary);
					color: var(--on-secondary);
				`;
			case "tertiary":
				return css`
					background: var(--tertiary);
					color: var(--on-tertiary);
				`;
			case "error":
				return css`
					background: ${() => getTheme().error};
					color: ${() => getTheme().surface_on};

					&:hover:enabled {
						background: ${() => getTheme().error_on};
					}
				`;
			case "destructive":
				return css`
					background: var(--error);
					color: #ffffff;

					&:hover:enabled {
						background: linear-gradient(
								0deg,
								var(--dark-tint) 0%,
								var(--dark-tint) 100%
							),
							var(--error);
					}
				`;
			case "outline":
				return css`
					background: transparent;
					border: 2px solid ${getTheme().outline_variant}!important;
					color: ${getTheme().surface_variant_on}!important;
					&:hover:enabled {
						background-color: ${getTheme().surface_container_highest}; /* Replace with a CSS variable if available */
					}
				`;
			case "text":
				return css`
					background: transparent;
					color: ${() => getTheme().title};
					padding: 0;
					height: auto;

					&:hover:enabled {
						color: ${() => getTheme().primary};
						text-decoration: underline;
					}

					&:active:enabled {
						color: ${() => getTheme().paragraph};
					}
				`;
			default:
				return "";
		}
	}}

  /* $disabled state */
  ${({ $disabled }) =>
		$disabled &&
		css`
			background: linear-gradient(
					0deg,
					var(--surface-container-lowest),
					var(--surface-container-lowest)
				),
				linear-gradient(0deg, var(--dark-tint), var(--dark-tint));
			cursor: not-allowed;
			color: var(--on-surface-$variant);
		`}

	/* $interactive state */
	${({ $interactive }) =>
		$interactive === false &&
		css`
			pointer-events: none!important;
		`}

	/* $busy state */
  ${({ $busy }) =>
		$busy &&
		css`
			pointer-events: none;
		`}

	/* $color state */
	${({ $color }) =>
		$color &&
		css`
			background-color: ${$color};
		`}

	/* $innerColor state */
	&:disabled {
		cursor: not-allowed;
	}
`;
